const DEFAULT_GAP_EM = 0.9;
const DEFAULT_STROKE_WIDTH = 1;
const DEFAULT_COLOR = '#EEEEF1';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const resolveLength = (value, element, fallback) => {
	const normalizedValue = `${value || ''}`.trim();
	const numericValue = Number.parseFloat(normalizedValue);

	if (Number.isNaN(numericValue)) {
		return fallback;
	}

	if (normalizedValue.endsWith('rem')) {
		const rootFontSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || fallback;
		return numericValue * rootFontSize;
	}

	if (normalizedValue.endsWith('em')) {
		const fontSize = Number.parseFloat(window.getComputedStyle(element).fontSize) || fallback;
		return numericValue * fontSize;
	}

	return numericValue;
};

const getMetrics = (canvas) => {
	const rect = canvas.getBoundingClientRect();
	const width = Math.round(rect.width);
	const height = Math.round(rect.height);

	if (!width || !height) {
		return null;
	}

	const context = canvas.getContext('2d');

	if (!context) {
		return null;
	}

	const styles = window.getComputedStyle(canvas);
	const bodyFontSize = Number.parseFloat(window.getComputedStyle(document.body).fontSize) || 20;
	const gap = resolveLength(styles.getPropertyValue('--striped-pattern-gap'), canvas, bodyFontSize * DEFAULT_GAP_EM);
	const strokeWidth = Math.max(
		1,
		Math.round(resolveLength(styles.getPropertyValue('--striped-pattern-stroke-width'), canvas, DEFAULT_STROKE_WIDTH)),
	);
	const repeat = Math.max(strokeWidth + gap, strokeWidth + 1);
	const devicePixelRatio = window.devicePixelRatio || 1;
	const nextCanvasWidth = Math.max(1, Math.round(width * devicePixelRatio));
	const nextCanvasHeight = Math.max(1, Math.round(height * devicePixelRatio));

	if (canvas.width !== nextCanvasWidth || canvas.height !== nextCanvasHeight) {
		canvas.width = nextCanvasWidth;
		canvas.height = nextCanvasHeight;
	}

	context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
	context.clearRect(0, 0, width, height);
	context.fillStyle = styles.getPropertyValue('--striped-pattern-color').trim() || DEFAULT_COLOR;

	return {
		context,
		width,
		height,
		strokeWidth,
		repeat,
		lineCount: Math.ceil(width / repeat),
	};
};

export function drawStripedPattern(canvas, getLineProgress = () => 1) {
	const metrics = getMetrics(canvas);

	if (!metrics) {
		return null;
	}

	const {
		context,
		width,
		height,
		strokeWidth,
		repeat,
		lineCount,
	} = metrics;

	for (let index = 0, x = 0; x < width; index += 1, x += repeat) {
		const progress = clamp(getLineProgress(index, lineCount), 0, 1);

		if (progress <= 0) {
			continue;
		}

		const lineHeight = Math.min(height, Math.max(1, Math.round(height * progress)));
		const y = index % 2 === 0 ? 0 : height - lineHeight;

		context.fillRect(Math.round(x), Math.round(y), strokeWidth, lineHeight);
	}

	return metrics;
}
